import React, { useState, useEffect, useRef } from "react"
import { useLang } from "hooks/useLang"
import Button from "components/Form/Button"

interface FadeOutEffectProps {
    children: React.ReactNode
    fadeStartAt?: number
    fadeHeight?: number
    externalLink?: string
}

const FadeOutEffect: React.FC<FadeOutEffectProps> = ({
    children,
    fadeStartAt = 400,
    fadeHeight = 200,
    externalLink
}) => {
    const { t } = useLang()
    const [isExpanded, setIsExpanded] = useState(false)
    const [showViewMore, setShowViewMore] = useState(false)
    const [maxHeight, setMaxHeight] = useState<string | number>(0)
    const contentRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const fadeEndAt = fadeStartAt + fadeHeight

    useEffect(() => {
        if (contentRef.current) {
            const scrollHeight = contentRef.current.scrollHeight
            setShowViewMore(scrollHeight > fadeEndAt)
            setMaxHeight(fadeEndAt)
        }
    }, [children, fadeEndAt])

    const toggleViewMore = () => {
        if (externalLink) {
            window.open(externalLink, "_blank")
            return
        }

        if (!isExpanded) {
            // expanding → animate to scrollHeight
            const fullHeight = contentRef.current?.scrollHeight || fadeEndAt
            setMaxHeight(fullHeight)
            setIsExpanded(true)

            // after animation → unlock height
            setTimeout(() => {
                setMaxHeight("none")
            }, 500) // match transition duration
        } else {
            // collapsing → first lock actual height, then shrink
            const fullHeight = contentRef.current?.scrollHeight || fadeEndAt
            setMaxHeight(fullHeight)
            requestAnimationFrame(() => {
                setMaxHeight(fadeEndAt)
            })
            setIsExpanded(false)

            // scroll back to start of content
            if (containerRef.current) {
                setTimeout(() => {
                    const headerOffset = 120
                    const elementPosition =
                        containerRef.current!.getBoundingClientRect().top
                    const offsetPosition =
                        elementPosition + window.pageYOffset - headerOffset

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth",
                    })
                }, 200)
            }
        }
    }

    const maskGradient = `linear-gradient(
    to bottom,
    rgba(0,0,0,1) ${fadeStartAt}px,
    rgba(0,0,0,0) ${fadeEndAt}px
  )`

    return (
        <div className="relative" ref={containerRef}>
            <div
                ref={contentRef}
                className="relative overflow-hidden"
                style={{
                    maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
                    transition: "max-height 0.5s ease",
                    maskImage: showViewMore && !isExpanded ? maskGradient : "none",
                    WebkitMaskImage: showViewMore && !isExpanded ? maskGradient : "none",
                    maskSize: "100% 100%",
                    WebkitMaskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",
                    maskPosition: "top",
                    WebkitMaskPosition: "top",
                }}
            >
                {children}
            </div>

            {showViewMore && (
                <Button
                    className={`bg-transparent border border-white rounded-md dark:text-white text-black opacity-60 cursor-pointer capitalize-first text-center px-5 text-xs py-1.5 hover:bg-transparent ${isExpanded ? "mt-3" : "mt-1.5"
                        }`}
                    onClick={toggleViewMore}
                >
                    {t(isExpanded ? "read_less" : "read_more")} {isExpanded ? "−" : "+"}
                </Button>
            )}
        </div>
    )
}

export default FadeOutEffect
